'use client'

import React, { useState } from 'react'
import { classify } from 'sidecar-utilities'

import { Icon, IconSize, IconType } from '@sidecar-components/atoms/Icon'
import { Sentiment } from '@sidecar-components/types'
import { ThumbnailProps } from './Thumbnail.types'

export const Thumbnail = ({
  alt,
  className = '',
  imgClassName = '',
  testId = '',
  icon = 'stethoscope',
  iconClass,
  iconType = IconType.CARE,
  photoSrc = '',
  fallbackSrc = '',
  sentiment = Sentiment.ACCENT,
  shape = 'circle',
  thumbnailSize = IconSize.MD,
  ...rest
}: ThumbnailProps) => {
  // Set icon and background colors based on sentiment
  let sentimentColor
  switch (sentiment) {
    case 'accent':
      sentimentColor = icon
        ? `color-accent-default
           [&_svg]:color-iconOnAccent-heavy-default`
        : null
      break
    case 'default':
      sentimentColor = icon
        ? `color-bgDefaultStrong
           [&_svg]:color-iconOnDefault`
        : null
      break
    case 'brand':
      sentimentColor = icon
        ? `color-engageOnBrand-default
           [&_svg]:color-iconEngage-heavy-default`
        : null
      break
    case 'notify':
      sentimentColor = icon
        ? `color-notify-default
           [&_svg]:color-iconNotify-heavy-default`
        : null
      break
    default:
      break
  }

  const widthHeight = {
    xxs: 'h-6 w-6',
    xs: 'h-8 w-8',
    sm: 'h-12 w-12',
    md: 'h-16 w-16',
    lg: 'h-20 w-20',
  }

  const baseStyle = classify({
    color: sentimentColor,
    layout: 'inline-flex justify-center items-center shrink-0',
    shape: shape === 'circle' ? 'rounded-full overflow-hidden' : null,
    size: thumbnailSize ? widthHeight[thumbnailSize] : null,
  })

  // The icon size is defined by its parent thumbnail size
  const iconSize = {
    xxs: 'xs',
    xs: 'sm',
    sm: 'md',
    md: 'lg',
    lg: 'xl',
  }

  // Try the sources in order — `photoSrc`, then `fallbackSrc` — and drop to the
  // glyph only once every image has failed. This lets a brand logo degrade to a
  // generic image (a pharmacy logo → the house pharmacy icon) instead of a bare
  // glyph when the logo URL is missing or 404s.
  const sources = [photoSrc, fallbackSrc].filter((src): src is string => !!src)
  const [srcIndex, setSrcIndex] = useState(0)
  const currentSrc = sources[srcIndex]
  const showImage = !!currentSrc
  const fallbackIcon =
    !!icon && !!thumbnailSize ? (
      <Icon
        name={icon}
        size={iconSize[thumbnailSize]}
        type={iconType}
        className={iconClass}
      />
    ) : null

  return (
    <div
      // imgClassName applies only while the image renders; the fallback icon
      // keeps the container's flex centering
      className={[baseStyle, showImage && imgClassName, className]
        .filter(Boolean)
        .join(' ')}
      data-testid={testId}
      {...rest}
    >
      {!showImage ? (
        fallbackIcon
      ) : (
        <img
          src={currentSrc}
          alt={alt}
          onError={() => {
            setSrcIndex(index => index + 1)
          }}
          // If the image has a transparency,
          // we don't want to show the blue/teal thumbnail bg color.
          // Instead, we want to show bg-white.
          // We don't want to use color-bgDefault because
          // then a black logo could dissappear on dark mode.
          // The max-w-fit has an ! to override's Google Maps adding a
          // max-width:none to its images.
          className="bg-white !max-w-fit h-full"
        />
      )}
    </div>
  )
}
